/**
 * Button Component
 * 
 * A styled button component with multiple variants matching the design system.
 */

import React, { forwardRef } from 'react';
import { Button as MuiButton, CircularProgress, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii, transitions, components } from '../../tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps {
  /** Button content */
  children: React.ReactNode;
  /** Button variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Click handler */
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Start icon */
  startIcon?: React.ReactNode;
  /** End icon */
  endIcon?: React.ReactNode;
  /** Full width */
  fullWidth?: boolean;
  /** Button type */
  type?: 'button' | 'submit' | 'reset';
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: colors.primary[500],
        color: colors.neutral[0],
        border: 'none',
        '&:hover': {
          backgroundColor: colors.primary[600],
        },
        '&:active': {
          backgroundColor: colors.primary[700],
        },
        '&:disabled': {
          backgroundColor: colors.neutral[200],
          color: colors.neutral[400],
        },
      };
    case 'secondary':
      return {
        backgroundColor: colors.neutral[0],
        color: colors.neutral[700],
        border: `1px solid ${colors.neutral[300]}`,
        '&:hover': {
          backgroundColor: colors.neutral[50],
          borderColor: colors.neutral[400],
        },
        '&:active': {
          backgroundColor: colors.neutral[100],
        },
        '&:disabled': {
          backgroundColor: colors.neutral[50],
          color: colors.neutral[400],
          borderColor: colors.neutral[200],
        },
      };
    case 'outline':
      return {
        backgroundColor: 'transparent',
        color: colors.primary[500],
        border: `1px solid ${colors.primary[500]}`,
        '&:hover': {
          backgroundColor: alpha(colors.primary[500], 0.08),
        },
        '&:active': {
          backgroundColor: alpha(colors.primary[500], 0.12),
        },
        '&:disabled': {
          color: colors.neutral[400],
          borderColor: colors.neutral[300],
        },
      };
    case 'ghost':
      return {
        backgroundColor: 'transparent',
        color: colors.neutral[700],
        border: 'none',
        '&:hover': {
          backgroundColor: colors.neutral[100],
        },
        '&:active': {
          backgroundColor: colors.neutral[150],
        },
        '&:disabled': {
          color: colors.neutral[400],
        },
      };
    case 'danger':
      return {
        backgroundColor: colors.error.main,
        color: colors.neutral[0],
        border: 'none',
        '&:hover': {
          backgroundColor: colors.error.dark,
        },
        '&:active': {
          backgroundColor: '#B91C1C',
        },
        '&:disabled': {
          backgroundColor: colors.neutral[200],
          color: colors.neutral[400],
        },
      };
    default:
      return {};
  }
};

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return {
        height: components.button.height.sm,
        padding: '6px 14px',
        fontSize: typographyTokens.fontSize.sm,
      };
    case 'medium':
      return {
        height: components.button.height.md,
        padding: '8px 20px',
        fontSize: typographyTokens.fontSize.base,
      };
    case 'large':
      return {
        height: components.button.height.lg,
        padding: '12px 28px',
        fontSize: typographyTokens.fontSize.md,
      };
    default:
      return {};
  }
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'medium',
  onClick,
  disabled = false,
  loading = false,
  startIcon,
  endIcon,
  fullWidth = false,
  type = 'button',
  sx,
}, ref) => {
  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  return (
    <MuiButton
      ref={ref}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      fullWidth={fullWidth}
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        ...variantStyles,
        ...sizeStyles,
        borderRadius: `${radii.md}px`,
        fontWeight: typographyTokens.fontWeight.medium,
        fontFamily: typographyTokens.fontFamily.body,
        textTransform: 'none',
        boxShadow: 'none',
        transition: transitions.fast,
        position: 'relative',
        '&:focus-visible': {
          outline: `2px solid ${colors.primary[500]}`,
          outlineOffset: 2,
        },
        ...sx,
      }}
    >
      {loading && (
        <CircularProgress
          size={18}
          sx={{
            position: 'absolute',
            color: variant === 'primary' || variant === 'danger' 
              ? colors.neutral[0] 
              : colors.primary[500],
          }}
        />
      )}
      <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
        {children}
      </span>
    </MuiButton>
  );
});

Button.displayName = 'Button';

export default Button;
